export type PayPalSdkIntent = "capture" | "subscription";

export interface LoadPayPalSdkOptions {
  clientId: string;
  intent: PayPalSdkIntent;
  currency?: string;
  vault?: boolean;
}

/** Builds the PayPal JS SDK URL for one-time payments or subscriptions. */
export function buildPayPalSdkUrl(options: LoadPayPalSdkOptions): string {
  const params = new URLSearchParams({
    "client-id": options.clientId,
    intent: options.intent,
  });

  if (options.intent === "capture" && options.currency) {
    params.set("currency", options.currency);
  }

  if (options.intent === "subscription" && options.vault) {
    params.set("vault", "true");
  }

  if (options.intent === "subscription") {
    params.set("enable-funding", "card");
  }

  return `https://www.paypal.com/sdk/js?${params.toString()}`;
}

let pendingPayPalSdkLoad: { url: string; promise: Promise<void> } | null = null;

/**
 * Loads the PayPal SDK for the requested intent.
 * Replaces an already-loaded SDK when intent/currency differ (e.g. subscription vs coin purchase).
 */
export function loadPayPalSdk(options: LoadPayPalSdkOptions): Promise<void> {
  const url = buildPayPalSdkUrl(options);
  const existing = document.querySelector(
    'script[data-flowchest-paypal-sdk="true"]'
  ) as HTMLScriptElement | null;

  if (existing?.src === url && typeof (window as any).paypal !== "undefined") {
    return Promise.resolve();
  }

  if (pendingPayPalSdkLoad?.url === url) {
    return pendingPayPalSdkLoad.promise;
  }

  document
    .querySelectorAll('script[src*="paypal.com/sdk"]')
    .forEach((node) => node.remove());
  delete (window as any).paypal;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.setAttribute("data-sdk-integration-source", "button-factory");
    script.setAttribute("data-flowchest-paypal-sdk", "true");
    script.onload = () => {
      if (pendingPayPalSdkLoad?.url === url) {
        pendingPayPalSdkLoad = null;
      }
      resolve();
    };
    script.onerror = () => {
      if (pendingPayPalSdkLoad?.url === url) {
        pendingPayPalSdkLoad = null;
      }
      reject(new Error("PayPal checkout could not be loaded."));
    };
    document.head.appendChild(script);
  });

  pendingPayPalSdkLoad = { url, promise };
  return promise;
}
